/*
 * Title: D1 Form - Column Help Modal
 * Description: Responsive modal explaining how each column of the
 *              requisition table is calculated, in plain language.
 */

import { useState } from 'react';
import { Modal } from 'react-bootstrap';

const COLUMN_HELP = [
    {
        title: 'Opening Balance',
        description:
            'The stock quantity available at the facility before the selected month began.',
        formula:
            '(Total received until last month) − (Supplied + Returned + Lost/Damaged/Expired until last month)',
    },
    {
        title: 'Total Quantity Received During The Month',
        description:
            'The total quantity of this medicine received from the main agency warehouse during the selected month.',
        formula: 'Sum of all received quantities dated within the month',
    },
    {
        title: 'Total Quantity Consumed By The Facility',
        description:
            'The total quantity dispensed to patients through OPD, IPD, Emergency and other departments during the month.',
        formula: 'Sum of all dispatch quantities dated within the month',
    },
    {
        title: 'Number Of Days Out Of Stock In The Pharmacy',
        description:
            'The number of days in the month when this medicine was unavailable in the pharmacy (stock at zero or below).',
        formula:
            'Day-by-day balance simulation — every day on which the running balance falls to 0 or below is counted.',
    },
    {
        title: 'Average Monthly Consumption',
        description:
            'The average quantity consumed per month over recent months, used to forecast future requirements.',
        formula:
            'Total consumption of the previous N months ÷ N (N = system setting, default 3 months)',
    },
    {
        title: 'Total Quantities Returned To Main Agency Warehouse',
        description:
            'The quantity sent back to the central warehouse during the month, usually due to over-stocking.',
        formula: 'Sum of return quantities dated within the month',
    },
    {
        title: 'Total Quantities Expired or Damaged',
        description:
            'The combined quantity written off during the month due to loss, damage, or expiry.',
        formula: 'Loss quantity + Damage quantity + Expired quantity (within the month)',
    },
    {
        title: 'Earliest Date of Expiry of Physical Stocks',
        description:
            'The nearest expiry date among all batches currently held in physical stock.',
        formula: 'Minimum expiry date across the batches in current stock',
    },
    {
        title: 'Theoretical Stock',
        description:
            'The stock quantity that should remain at the end of the month according to the records.',
        formula:
            '(Opening Balance + Received) − (Consumed + Returned + Expired/Damaged)',
    },
    {
        title: 'Closing Balance (Physical Stock)',
        description:
            'The recorded stock balance at the end of the month; currently equal to the theoretical stock.',
        formula: 'Same as Theoretical Stock',
    },
    {
        title: 'Number of Days of Physical Stock Available',
        description:
            'How many more days the remaining stock is expected to last at the current rate of consumption.',
        formula:
            '(Theoretical Stock ÷ Average Monthly Consumption) × 30 days',
    },
];

export default function ColumnHelpModal() {
    const [show, setShow] = useState(false);

    return (
        <>
            <i
                className="fas fa-info-circle text-primary ms-2 align-middle"
                role="button"
                tabIndex={0}
                aria-label="How the columns are calculated"
                title="How the columns are calculated"
                style={{ cursor: 'pointer', fontSize: '0.75em' }}
                onClick={() => setShow(true)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') setShow(true);
                }}
            />

            <Modal
                show={show}
                onHide={() => setShow(false)}
                size="lg"
                centered
                scrollable
            >
                <Modal.Header closeButton>
                    <Modal.Title>How the Columns Are Calculated</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted mb-4">
                        The table below explains what each column of the
                        requisition form means and how the system calculates
                        its value.
                    </p>

                    {COLUMN_HELP.map((item, index) => (
                        <div key={item.title} className="mb-3 pb-3 border-bottom">
                            <div className="d-flex align-items-start">
                                <span className="badge bg-primary me-2 mt-1">
                                    {index + 1}
                                </span>
                                <div>
                                    <strong>{item.title}</strong>
                                    <p className="mb-1">{item.description}</p>
                                    <small className="text-muted d-block">
                                        <em>Formula:</em> {item.formula}
                                    </small>
                                </div>
                            </div>
                        </div>
                    ))}
                </Modal.Body>
            </Modal>
        </>
    );
}
